import {
  FileType,
  SearchDealQuery,
  useChunkSnippetQuery,
} from "@/src/graphql/generated";
import {
  ParentFolder,
  ResultItemHeaderWrapper,
  SearchResultCategory,
  SeparationDot,
} from "./search_result";
import { formatDistanceToNowStrict, fromUnixTime } from "date-fns";
import { FileIcon } from "../../FileIcon";
import { HighlightedText } from "../../HighlightedText";
import { useEffect } from "react";
import useGqlClient from "@/src/hooks/useGqlClient";
import { rectToExcelRange } from "@/src/utils/rectToExcelRange";
import { useDispatch } from "react-redux";
import { actions } from "@/src/store/deal-search/slice";
import { Spinner } from "../../icons/Spinner";
import { ExcelSnippet } from "../../ExcelSnippet";

interface Props {
  result: SearchDealQuery["searchDeal"][0]["chunk"];
  searchTerm: string;
  isFocused: boolean;
}

export function SearchResultChunk(props: Props) {
  const { result, searchTerm } = props;

  if (!result) {
    return null;
  }

  if (!result.dataRoomFile) {
    return null;
  }

  const file = result.dataRoomFile;

  return (
    <div className="flex items-start py-2">
      <SearchResultCategory category="file" />
      <div className="ml-3">
        <ResultItemHeaderWrapper>
          <ParentFolder folder={file.parentFolder} />
          <SeparationDot />
          <p className="text-xs/none text-gray-500">
            Created{" "}
            {formatDistanceToNowStrict(fromUnixTime(file.createdAt), {
              addSuffix: true,
            })}{" "}
            by {file.createdBy.name}
          </p>
        </ResultItemHeaderWrapper>
        <div className=" flex items-center gap-2 ">
          <FileIcon fileType={file.fileType} />
          <HighlightedText
            text={file.name}
            searchTerm={searchTerm}
            className="text-sm text-gray-700 font-semibold"
          />
          {result.excel || result.location ? (
            <span className="text-xs text-gray-500">{`\u2022`}</span>
          ) : null}
          {result.excel ? (
            <span className="text-xs text-gray-500">
              {result.excel.sheetName} (
              {rectToExcelRange({
                row0: result.excel.row0,
                row1: result.excel.row1,
                col0: result.excel.col0,
                col1: result.excel.col1,
              })}
              )
            </span>
          ) : null}
        </div>
        {result.excel ? (
          <ExcelSnippet
            isFocused={props.isFocused}
            content={result.excel.csv}
            searchTerm={searchTerm}
          />
        ) : null}
        {[FileType.Docx, FileType.Pdf].includes(
          result.dataRoomFile.fileType,
        ) ? (
          <ChunkSnippet id={result.id} query={searchTerm} />
        ) : null}
        {/* <Card>
          <Markdown
            className="max-w-[calc(100%-200px)] overflow-y-scroll"
            remarkPlugins={[remarkGfm]}
          >
            {result.content}
          </Markdown>
        </Card> */}
      </div>
    </div>
  );
}

function ChunkSnippet(props: { id: string; query: string }) {
  const { id, query } = props;
  const dispatch = useDispatch();

  const client = useGqlClient();
  const { data, isLoading, error } = useChunkSnippetQuery(client, {
    id,
    query,
  });

  useEffect(() => {
    if (isLoading || error) {
      return;
    }

    if (!data) {
      return;
    }

    const firstSnippet = data.chunkSnippet[0];
    dispatch(
      actions.addSnippet({
        snippet: firstSnippet,
        searchTerm: props.query,
        documentChunkID: id,
      }),
    );
  }, [data, isLoading, error]);

  if (error) {
    return <div>Error</div>;
  }

  if (isLoading || !data) {
    return (
      <div className="mt-2 border border-concrete-200 shadow-sm rounded-md p-2 flex items-center gap-x-2">
        <Spinner size="s" color="gray" withMargin={false} />
        <p className="text-sm text-gray-500 font-semibold">
          Finding relevant quote...
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2 mt-3 mr-2">
      {data.chunkSnippet.map((cs) => {
        return (
          <div className="p-2 border border-concrete-200 rounded-md shadow-sm">
            <p className="text-xs text-gray-500">
              Page {cs.location.pageIndex + 1}
            </p>
            <HighlightedText searchTerm={query} text={cs.quote} />
          </div>
        );
      })}
    </div>
  );
}
